import React, { useState, useCallback, useEffect, useRef } from 'react'
import Picker, { PickerValue } from 'react-mobile-picker'
import Button from 'components/Button'
import './index.scss'

interface InputDateProps {
  value: string
  label: string
  error?: string
  today?: boolean
  onChange: (date: string) => void
}

function getDayArray(year: number, month: number) {
  const dayCount = new Date(year, month, 0).getDate()
  return Array.from({ length: dayCount }, (_, i) => String(i + 1).padStart(2, '0'))
}

const DateButton: React.FC<{ children: string, selected: boolean }> = ({ children, selected }) => (
  <div
    className='dateButton'
    style={{ fontWeight: selected ? 'bold' : 'normal', color: selected ? '#000' : '#888' }}>
    {children}
  </div>
)

const InputDate: React.FC<InputDateProps> = ({ value, label, error, today, onChange }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [pickerValue, setPickerValue] = useState<PickerValue>(() => {
    const todayC = new Date()
    const yearC = todayC.getFullYear().toString()
    const monthC = String(todayC.getMonth() + 1).padStart(2, '0')
    const dayC = String(todayC.getDate()).padStart(2, '0')
    const [year, month, day] = value ? value.split('-') : today ? [yearC, monthC, dayC] : ['1950', '06', '06']
    return { year, month, day }
  })

  const currentYear = useRef(new Date().getFullYear())
  const handlePickerChange = useCallback((newValue: PickerValue, key: string) => {
    if (key === 'day') {
      setPickerValue(newValue)
      return
    }

    const { year, month } = newValue
    const newDayArray = getDayArray(Number(year), Number(month))
    const newDay = newDayArray.includes(newValue.day) ? newValue.day : newDayArray[newDayArray.length - 1]
    setPickerValue({ ...newValue, day: newDay })
  }, [])

  const handleConfirm = () => {
    const newDate = `${pickerValue.year}-${pickerValue.month}-${pickerValue.day}`
    onChange(newDate)
    setIsOpen(false)
  }

  useEffect(() => {
    if (isOpen) {
      document.body.style.setProperty('overflow', 'hidden')
      return () => {
        document.body.style.removeProperty('overflow')
      }
    }
  }, [isOpen])


  const startYear = (() => {
    const selectedYear = Number(pickerValue.year)
    const start = selectedYear + 20
    return (start > currentYear.current)
      ? currentYear.current
      : (start < 40)
        ? 40
        : start
  })()

  return (
    <>
      <div className={`myInput myDateInput ${error ? 'error' : ''}`} onClick={() => setIsOpen(true)}>
        <label>{error ? error : label}</label>
        <div>
          {value === ""
            ? <span style={{ color: 'rgba(41, 41, 41, 0.3)' }}>Kliknij, aby wybrać datę</span>
            : <span>{pickerValue.day}.{pickerValue.month}.{pickerValue.year}</span>}
          <i className="icon icon--calendar"></i>
        </div>
      </div>

      {isOpen && (
        <div className='dateModal'>
          <div className='dateModal__content'>
            <div className="title">
              <i className="icon icon--calendar"></i>
              <h1>Wybierz datę</h1>
            </div>

            <div className='head'>
              <span>Dzień</span>
              <span>Miesiąc</span>
              <span>Rok</span>
            </div>

            <Picker
              value={pickerValue}
              onChange={handlePickerChange}
              wheelMode="natural">
              <Picker.Column name="day">
                {getDayArray(Number(pickerValue.year), Number(pickerValue.month)).map((day) => (
                  <Picker.Item key={day} value={day}>
                    {({ selected }) => (
                      <DateButton selected={selected}>
                        {day}
                      </DateButton>
                    )}
                  </Picker.Item>
                ))}
              </Picker.Column>

              <Picker.Column name="month">
                {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0')).map((month) => (
                  <Picker.Item key={month} value={month}>
                    {({ selected }) => (
                      <DateButton selected={selected}>
                        {month}
                      </DateButton>
                    )}
                  </Picker.Item>
                ))}
              </Picker.Column>

              <Picker.Column name="year">
                {Array.from({ length: 41 }, (_, i) => `${startYear - i}`).map((year) => (
                  <Picker.Item key={year} value={year}>
                    {({ selected }) => (
                      <DateButton selected={selected}>
                        {year}
                      </DateButton>
                    )}
                  </Picker.Item>
                ))}
              </Picker.Column>
            </Picker>

            <Button type='primary' text='Gotowe' action={handleConfirm} />
          </div>
        </div>
      )}
    </>
  )
}

export default InputDate